import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { Form, FormRenderProps } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FORM_ERROR, setIn } from "final-form";
import { OtlAutosave } from "./OtlAutosave";
import { OtlSubmissionErrors } from "./OtlSubmissionErrors";
import { OtlDraftRecovery, useOtlDraftRecovery } from "./useOtlDraftRecovery";
import { OtlFormSubmission } from "./OtlFormSubmission";
import { OtlSubmissionError, submitOTL } from "../common/api/otlAPi";
import {
  Attempt,
  Draft,
  DRAFT_KEY,
  DISABLED_KEY,
  writeDraft,
} from "./otlDraft";

interface OtlSubmissionFormProps {
  period: string;
  initialValues: Partial<OtlFormSubmission>;
  children: (
    props: FormRenderProps<OtlFormSubmission> & { locked: boolean },
  ) => React.ReactNode;
}

export function OtlSubmissionForm(props: OtlSubmissionFormProps) {
  const recovery = useOtlDraftRecovery(props.period);
  if (!recovery)
    return (
      <Typography role="status">Checking for a saved application…</Typography>
    );
  return (
    <ReadySubmissionForm key={props.period} {...props} recovery={recovery} />
  );
}

function ReadySubmissionForm({
  period,
  initialValues,
  children,
  recovery,
}: OtlSubmissionFormProps & { recovery: OtlDraftRecovery }) {
  const [offered, setOffered] = useState<Draft | undefined>(recovery.draft);
  const [values, setValues] = useState(initialValues);
  const [enabled, setEnabled] = useState(recovery.enabled);
  const [status, setStatus] = useState(
    recovery.error
      ? "Saving is unavailable on this browser. Keep this page open until receipt is confirmed."
      : recovery.enabled
        ? ""
        : "Saving is disabled on this device.",
  );
  const [attempt, setAttempt] = useState<Attempt>();
  const attemptRef = useRef<Attempt>();
  const [receipt, setReceipt] = useState<string>();
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [retryConsent, setRetryConsent] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const completed = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const fieldsContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (receipt) heading.current?.focus();
  }, [receipt]);
  const save = React.useCallback(
    (current: Partial<OtlFormSubmission>) => {
      if (!enabled || completed.current || offered) return;
      try {
        writeDraft(localStorage, period, current, attemptRef.current);
        setStatus("Saved on this device");
      } catch {
        setStatus(
          "Unable to save on this device. Keep this page open until receipt is confirmed.",
        );
      }
    },
    [enabled, period, offered],
  );
  const onDirty = React.useCallback(() => setStatus("Saving…"), []);
  const erase = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      return true;
    } catch {
      setStatus(
        "Unable to erase browser storage. Clear this site’s data in your browser settings.",
      );
      return false;
    }
  };
  const submit = async (current: OtlFormSubmission) => {
    setError("");
    setFieldErrors({});
    let pending = attemptRef.current;
    if (!pending) {
      try {
        pending = {
          submissionKey: crypto.randomUUID(),
          snapshot: JSON.parse(JSON.stringify(current)),
        };
      } catch {
        setError(
          "This browser cannot create a secure submission reference. Please use a current browser.",
        );
        return { [FORM_ERROR]: "Unable to submit" };
      }
      attemptRef.current = pending;
      setAttempt(pending);
      save(current);
    }
    try {
      const result = await submitOTL({
        ...pending.snapshot,
        submissionKey: pending.submissionKey,
      });
      completed.current = true;
      erase();
      setReceipt(result.reference);
      return undefined;
    } catch (failure) {
      if (failure instanceof OtlSubmissionError && failure.status === 422) {
        attemptRef.current = undefined;
        setAttempt(undefined);
        save(current);
        setFieldErrors(failure.errors);
        setError("Please correct the fields below and submit again.");
        return Object.entries(failure.errors).reduce<object>(
          (errors, [path, messages]) => setIn(errors, path, messages.join(" ")),
          { [FORM_ERROR]: failure.message },
        );
      }
      setError(
        failure instanceof OtlSubmissionError && failure.status === 409
          ? "This submission reference conflicts with an existing request. Contact ontheland@inuvialuit.com before submitting another application."
          : "We could not confirm receipt. Retry the original submission below before editing or starting another application.",
      );
      return { [FORM_ERROR]: "Receipt not confirmed" };
    }
  };
  if (receipt)
    return (
      <Stack gap={2} role="status">
        <Typography variant="h4" component="h1" tabIndex={-1} ref={heading}>
          Your On the Land application has been received.
        </Typography>
        <Typography>
          A confirmation email will be sent to the email address you provided.
        </Typography>
        <Typography>Reference: {receipt}</Typography>
        {status.includes("Unable to erase") && (
          <Alert severity="warning">{status}</Alert>
        )}
      </Stack>
    );
  if (offered)
    return (
      <Stack gap={2}>
        <Typography>
          A saved application is available on this browser. Drafts expire seven
          days after the last edit.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setValues({ ...offered.values, releaseAccept: false });
            attemptRef.current = offered.attempt;
            setAttempt(offered.attempt);
            setRetryConsent(!offered.attempt);
            setOffered(undefined);
          }}
        >
          Resume saved application
        </Button>
        {offered.attempt ? (
          <Alert severity="warning">
            This application has an unconfirmed submission. Resume and retry it
            to confirm its receipt before starting another application.
          </Alert>
        ) : (
          <Button
            onClick={() => {
              erase();
              setOffered(undefined);
            }}
          >
            Start over
          </Button>
        )}
      </Stack>
    );
  return (
    <Form<OtlFormSubmission>
      initialValues={values}
      mutators={{ ...arrayMutators }}
      onSubmit={submit}
      render={(props) => (
        <>
          {recovery.discardedDraft && (
            <Alert severity="info">
              The previous saved application expired or could not be restored
              for this funding period. If you previously submitted without
              receiving a receipt, contact ontheland@inuvialuit.com before
              starting another application.
            </Alert>
          )}
          <Box
            mb={2}
            p={2}
            pb={1}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Typography variant="body2">
              Optional drafts are stored in this browser and profile for seven
              days. Anyone using this profile may see them. Internet access is
              needed to reopen the form.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabled}
                  disabled={props.submitting || retrying}
                  onChange={(_, checked) => {
                    setEnabled(checked);
                    let preferenceStored = true;
                    try {
                      localStorage.setItem(DISABLED_KEY, String(!checked));
                    } catch {
                      preferenceStored = false;
                      setStatus(
                        "Your saving preference could not be stored. Check this setting when you reopen the form.",
                      );
                    }
                    if (!checked && erase() && preferenceStored) {
                      setStatus("Saving disabled; saved draft erased.");
                    }
                  }}
                />
              }
              label="Save on this device"
            />
            {/* <Typography role="status" variant="body2">
              {status}
            </Typography> */}
          </Box>
          {enabled && <OtlAutosave save={save} onDirty={onDirty} />}
          {error && (
            <OtlSubmissionErrors
              error={error}
              fieldErrors={fieldErrors}
              fieldsContainer={fieldsContainer}
            />
          )}
          {attempt && !props.submitting && (
            <Box my={2}>
              {!retryConsent && (
                <Typography>
                  Review the privacy policy below and agree again before
                  retrying your original application.
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={retryConsent}
                    onChange={(_, checked) => setRetryConsent(checked)}
                  />
                }
                label="I agree to the privacy policy and consent shown below for this original submission."
              />
              <Button
                variant="contained"
                disabled={retrying || !retryConsent}
                onClick={async () => {
                  setRetrying(true);
                  try {
                    await submit(attempt.snapshot);
                  } finally {
                    setRetrying(false);
                  }
                }}
              >
                {retrying ? "Confirming receipt…" : "Retry original submission"}
              </Button>
            </Box>
          )}
          <div ref={fieldsContainer}>
            {children({ ...props, locked: Boolean(attempt) || retrying })}
          </div>
        </>
      )}
    />
  );
}
